import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { X, Check } from 'lucide-react';

export default function InviteAdminModal({ onClose, onSent }) {
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      const res = await fetch(`${API_URL}/admin/invite-admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({
          email: form.email,
          first_name: form.firstName,
          last_name: form.lastName,
        }),
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to invite admin');

      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13, 167, 202,0.15)]';

  return (
    <div className="fixed inset-0 bg-overlay backdrop-blur-lg flex items-center justify-center z-[200] animate-fade-in p-4" onClick={onClose}>
      <div className="w-full max-w-[440px] bg-elevated rounded-xl border border-theme shadow-2xl animate-modal-in" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-theme">
          <h3 className="text-[18px] font-bold tracking-tight text-primary-c">Invite Platform Admin</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-tertiary text-muted-c flex items-center justify-center hover:bg-hover-surface hover:text-primary-c transition-all">
            <X size={14} strokeWidth={2.2} />
          </button>
        </div>

        <div className="px-6 py-6">
          {success ? (
            <div className="text-center py-4">
              <div className="w-[48px] h-[48px] bg-success-soft text-success rounded-[14px] flex items-center justify-center mx-auto mb-4"><Check size={24} strokeWidth={2.5} /></div>
              <h4 className="text-[16px] font-semibold text-primary-c mb-1">Admin invited</h4>
              <p className="text-[13px] text-muted-c mb-4">
                An invitation has been sent to <strong className="text-primary-c">{form.email}</strong> with admin access.
              </p>
              <button onClick={() => { onSent?.(); onClose(); }} className="px-4 py-2 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow">
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && <div className="px-4 py-3 rounded-[10px] bg-danger-soft text-danger text-[13px] font-medium">{error}</div>}

              <p className="text-[13px] text-secondary-c">
                Enter their details to create an admin account and send an invitation email.
              </p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] font-semibold text-secondary-c mb-2">First Name</label>
                  <input type="text" className={inputClass} value={form.firstName} onChange={e => update('firstName', e.target.value)} required placeholder="John" />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-secondary-c mb-2">Last Name</label>
                  <input type="text" className={inputClass} value={form.lastName} onChange={e => update('lastName', e.target.value)} required placeholder="Doe" />
                </div>
              </div>

              <div>
                <label className="block text-[12px] font-semibold text-secondary-c mb-2">Email Address</label>
                <input type="email" className={inputClass} value={form.email} onChange={e => update('email', e.target.value)} required placeholder="admin@example.com" autoFocus />
              </div>

              <div className="flex justify-end gap-2.5 pt-4 border-t border-theme">
                <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-primary-c bg-tertiary border border-theme hover:bg-hover-surface transition-all">
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50">
                  {loading ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
